import { Box, Flex, Text } from '@chakra-ui/react'

import IconCheckCircle from '@/components/icons/IconCheckCircle'
import IconClock from '@/components/icons/IconClock'
import IconClose from '@/components/icons/IconClose'
import IconExclamationCircle from '@/components/icons/IconExclamationCircle'
import IconRetry from '@/components/icons/IconRetry'
import { Tooltip } from '@/components/ui/Tooltip'

import type { FileStatus } from '@/hooks/useMultiFileUpload'

interface FileRowProps {
  name: string
  status: FileStatus
  id: string
  onRemove: (id: string) => void
  onRetry?: (id: string) => void
}

export default function FileRow({
  name,
  status,
  id,
  onRemove,
  onRetry
}: FileRowProps) {
  const isDone = status === 'done'
  const isPending = status === 'pending'
  const isError = status === 'error'
  const isUploadingFile = status === 'uploading'

  return (
    <Flex direction="column" gap="7px">
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={2}>
          {isDone && (
            <Tooltip content="Successfully uploaded" positioning={{ placement: 'left' }}>
              <Box display="inline-flex" alignItems="center" flexShrink={0}>
                <IconCheckCircle boxSize="16px" color="#1C8D02" />
              </Box>
            </Tooltip>
          )}
          {(isPending || isUploadingFile) && (
            <Tooltip
              content={isUploadingFile ? 'Uploading' : 'Pending'}
              positioning={{ placement: 'left' }}
            >
              <Box display="inline-flex" alignItems="center" flexShrink={0}>
                <IconClock boxSize="16px" color="text.tertiary" />
              </Box>
            </Tooltip>
          )}
          {isError && (
            <Tooltip content="Connection lost" positioning={{ placement: 'left' }}>
              <Box display="inline-flex" alignItems="center" flexShrink={0}>
                <IconExclamationCircle boxSize="16px" color="red.500" />
              </Box>
            </Tooltip>
          )}
          <Text
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            textStyle="bodySmall"
          >
            {name}
          </Text>
        </Flex>

        {isError && (
          <Flex align="center" gap={2}>
            {onRetry && (
              <Tooltip content="Retry" positioning={{ placement: 'left' }}>
                <Box
                  display="inline-flex"
                  alignItems="center"
                  cursor="pointer"
                  flexShrink={0}
                  onClick={() => onRetry(id)}
                >
                  <IconRetry boxSize="18px" color="text.tertiary" />
                </Box>
              </Tooltip>
            )}
            <Tooltip content="Remove" positioning={{ placement: 'left' }}>
              <Box
                display="inline-flex"
                alignItems="center"
                cursor="pointer"
                flexShrink={0}
                onClick={() => onRemove(id)}
              >
                <IconClose boxSize="18px" color="text.tertiary" />
              </Box>
            </Tooltip>
          </Flex>
        )}
        {(isPending || isDone) && (
          <Tooltip content="Remove" positioning={{ placement: 'left' }}>
            <Box
              display="inline-flex"
              alignItems="center"
              cursor="pointer"
              flexShrink={0}
              onClick={() => onRemove(id)}
            >
              <IconClose boxSize="18px" color="text.tertiary" />
            </Box>
          </Tooltip>
        )}
      </Flex>

      <Box h="3px" w="full" bg="border.subtle" borderRadius="4px">
        <Box
          h="full"
          bg={
            isDone
              ? 'green.600'
              : isUploadingFile
                ? 'green.600'
                : isError
                  ? 'red.500'
                  : 'border.subtle'
          }
          w={
            isDone ? '100%' : isUploadingFile ? '60%' : isError ? '100%' : '0%'
          }
          borderRadius="4px"
          transition="width 0.3s ease"
        />
      </Box>
    </Flex>
  )
}
